import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ListMessageThreadsQuery } from '@emr/shared';

import { listThreads } from '../api/messaging.ts';
import { searchPatients } from '../api/patients.ts';
import { useOrgMembers } from '../schedule/useOrgMembers.ts';
import { useSession } from '../auth/sessionContext.ts';
import { FailureNotice, LoadingNotice } from '../components/AsyncState.tsx';
import { Field, Panel, SelectField } from '../components/primitives.tsx';
import { GatedRoute } from '../components/GatedRoute.tsx';
import { useHasRoomForInboxSplitView } from '../hooks/useMediaQuery.ts';
import { ThreadDetail } from '../inbox/ThreadDetail.tsx';
import { ThreadList } from '../inbox/ThreadList.tsx';

/**
 * The staff secure-messaging inbox (`BACKLOG.md` W2, pillar 3, `docs/decisions/patient-portal-
 * messaging-scope.md`) — a cross-patient work queue, the same register as `SchedulePage.tsx`, hence
 * a top-level sibling route rather than a chart tab (the decision doc's own "Staff-side UI
 * implication" section states this directly).
 *
 * **Two-pane on desktop, one pane at a time on a phone — a real content swap, not a squeeze.**
 * `useHasRoomForInboxSplitView` governs which; see that hook's own header for how the breakpoint was
 * measured. This mirrors `SchedulePage.tsx`'s own `useHasRoomForCalendarGrid`/`AppointmentList`
 * fallback shape and `shell/AppShell.tsx`'s "navigation is conditionally mounted, never CSS-toggled"
 * discipline — exactly one layout exists in the document at a time, decided in JS, never two DOM
 * copies toggled with a Tailwind breakpoint class alongside a JS one that might disagree with it.
 *
 * **Distinguishing `care_team` from `general` threads is the list/detail panes' own job**
 * (`inbox/ThreadList.tsx`, `inbox/ThreadDetail.tsx`, `inbox/threadRules.ts`), not this page's — this
 * component owns query state (filters, which thread is open) and the responsive layout only.
 *
 * **"By patient" (the decision doc's own filter list: "mine / unassigned / by patient / by kind") is
 * a client-side filter, not a server query param** — `listMessageThreadsQuerySchema` (`@emr/shared`)
 * has no `patientId` filter, so this page fetches the caller's already-visible threads (server-side
 * confidentiality-filtered, same as every other filter here) and narrows them locally against the
 * batch-prefetched patient directory below, the same "fetch once, filter/search client-side" shape
 * `routes/patients/PatientDuplicatesPage.tsx` already uses for its own candidate list.
 */
export function InboxPage() {
  const { t } = useTranslation('inbox');
  return (
    <GatedRoute
      title={t('page.title')}
      capability="patient:read"
      feature={null}
      helpTopicId="secure-messaging"
    >
      <InboxContent />
    </GatedRoute>
  );
}

const KIND_FILTERS = ['all', 'care_team', 'general'] as const;
type KindFilter = (typeof KIND_FILTERS)[number];
function isKindFilter(value: string): value is KindFilter {
  return (KIND_FILTERS as readonly string[]).includes(value);
}

const STATUS_FILTERS = ['open', 'closed'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];
function isStatusFilter(value: string): value is StatusFilter {
  return (STATUS_FILTERS as readonly string[]).includes(value);
}

const ASSIGNED_FILTERS = ['all', 'mine', 'unassigned'] as const;
type AssignedFilter = (typeof ASSIGNED_FILTERS)[number];
function isAssignedFilter(value: string): value is AssignedFilter {
  return (ASSIGNED_FILTERS as readonly string[]).includes(value);
}

function InboxContent() {
  const { t } = useTranslation('inbox');
  const session = useSession();
  const hasRoomForSplit = useHasRoomForInboxSplitView();
  const members = useOrgMembers();
  const queryClient = useQueryClient();

  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [assignedFilter, setAssignedFilter] = useState<AssignedFilter>('all');
  const [patientSearch, setPatientSearch] = useState('');
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);

  // `assigned` only has an effect within the server's own `general`-thread branch (`messaging/
  // service.ts`'s `listGeneralThreadsForStaff`) — omitted entirely once `kindFilter === 'care_team'`
  // so the request never implies a filter this scope can't honor.
  const query: ListMessageThreadsQuery = {
    ...(kindFilter === 'all' ? {} : { kind: kindFilter }),
    status: statusFilter,
    ...(kindFilter !== 'care_team' && assignedFilter !== 'all' ? { assigned: assignedFilter } : {}),
  };

  const threadsQuery = useQuery({
    queryKey: ['inbox', 'threads', kindFilter, statusFilter, assignedFilter],
    queryFn: ({ signal }: { signal: AbortSignal }) => listThreads(query, signal),
  });

  // Batch-prefetch every distinct patient this page's threads reference, once, matching
  // `SchedulePage.tsx`'s own `BACKLOG.md` K5 reasoning — an inbox page with 20-30 open threads
  // otherwise means 20-30 concurrent per-row `GET /api/patients/:patientId` calls from
  // `PatientLabel`'s own `usePatient`. Populates the same `['patient', id]` cache key that hook
  // already reads (so no downstream call site needs to know this happened) *and* this page's own
  // `patientsById` state below, which the "by patient" filter reads directly — kept as real state
  // rather than a `queryClient.getQueryData` read inside a `useMemo`, since the latter has nothing
  // in its own dependency array that changes when a cache *write* lands (the fetched list's own
  // identity does not change), which is exactly the kind of dependency `eslint-plugin-react-hooks`
  // correctly refuses to accept as "used".
  const [patientsById, setPatientsById] = useState<
    ReadonlyMap<
      string,
      { readonly lastName: string; readonly firstName: string; readonly mrn: string }
    >
  >(new Map());

  useEffect(() => {
    if (threadsQuery.data === undefined || threadsQuery.data.length === 0) return;
    const ids = [...new Set(threadsQuery.data.map((thread) => thread.patientId))];
    const controller = new AbortController();
    searchPatients({ ids }, controller.signal)
      .then((patients) => {
        for (const patient of patients) {
          queryClient.setQueryData(['patient', patient.id], patient);
        }
        setPatientsById((current) => {
          const next = new Map(current);
          for (const patient of patients) next.set(patient.id, patient);
          return next;
        });
      })
      .catch(() => {
        // Cache-warming prefetch only — a failure here just means PatientLabel's own usePatient
        // calls fall back to their normal per-row fetch, same as SchedulePage's identical catch.
      });
    return () => {
      controller.abort();
    };
  }, [threadsQuery.data, queryClient]);

  const filteredThreads = useMemo(() => {
    const data = threadsQuery.data ?? [];
    const search = patientSearch.trim().toLowerCase();
    if (search === '') return data;
    return data.filter((thread) => {
      const patient = patientsById.get(thread.patientId);
      if (patient === undefined) return false;
      const haystack = `${patient.lastName} ${patient.firstName} ${patient.mrn}`.toLowerCase();
      return haystack.includes(search);
    });
  }, [threadsQuery.data, patientSearch, patientsById]);

  const filtersPanel = (
    <Panel title={t('page.filters.panelTitle')}>
      <div className="flex flex-col gap-3">
        <SelectField
          label={t('page.filters.kindLabel')}
          value={kindFilter}
          onChange={(event) => {
            const { value } = event.target;
            if (isKindFilter(value)) setKindFilter(value);
          }}
        >
          {KIND_FILTERS.map((kind) => (
            <option key={kind} value={kind}>
              {t(`page.filters.kindOption.${kind}`)}
            </option>
          ))}
        </SelectField>

        <SelectField
          label={t('page.filters.statusLabel')}
          value={statusFilter}
          onChange={(event) => {
            const { value } = event.target;
            if (isStatusFilter(value)) setStatusFilter(value);
          }}
        >
          {STATUS_FILTERS.map((status) => (
            <option key={status} value={status}>
              {t(`page.filters.statusOption.${status}`)}
            </option>
          ))}
        </SelectField>

        <SelectField
          label={t('page.filters.assignedLabel')}
          value={assignedFilter}
          disabled={kindFilter === 'care_team'}
          onChange={(event) => {
            const { value } = event.target;
            if (isAssignedFilter(value)) setAssignedFilter(value);
          }}
          {...(kindFilter === 'care_team' ? { hint: t('page.filters.assignedDisabledHint') } : {})}
        >
          {ASSIGNED_FILTERS.map((assigned) => (
            <option key={assigned} value={assigned}>
              {t(`page.filters.assignedOption.${assigned}`)}
            </option>
          ))}
        </SelectField>

        <Field
          label={t('page.filters.patientSearchLabel')}
          placeholder={t('page.filters.patientSearchPlaceholder')}
          value={patientSearch}
          onChange={(event) => {
            setPatientSearch(event.target.value);
          }}
        />
      </div>
    </Panel>
  );

  const listSection = threadsQuery.isPending ? (
    <LoadingNotice label={t('page.list.loading')} />
  ) : threadsQuery.isError ? (
    <FailureNotice
      error={threadsQuery.error}
      onRetry={() => {
        void threadsQuery.refetch();
      }}
    />
  ) : (
    <ThreadList
      threads={filteredThreads}
      selectedThreadId={hasRoomForSplit ? selectedThreadId : null}
      onSelect={setSelectedThreadId}
      viewerUserId={session.userId}
      members={members.data}
    />
  );

  if (hasRoomForSplit) {
    return (
      <div className="flex items-start gap-4">
        <div className="flex w-full max-w-sm shrink-0 flex-col gap-4">
          {filtersPanel}
          {listSection}
        </div>
        <div className="min-w-0 flex-1">
          {selectedThreadId !== null ? (
            <ThreadDetail threadId={selectedThreadId} />
          ) : (
            <Panel>
              <p className="m-0 text-sm text-ink-muted">{t('page.detail.selectPrompt')}</p>
            </Panel>
          )}
        </div>
      </div>
    );
  }

  if (selectedThreadId !== null) {
    return (
      <ThreadDetail
        threadId={selectedThreadId}
        onBack={() => {
          setSelectedThreadId(null);
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {filtersPanel}
      {listSection}
    </div>
  );
}
